import { useEffect, useState } from "react";
import { X, History, Trash2, Edit, Save, XCircle } from "lucide-react";
import { supabase } from "../lib/supabaseClient.ts";

type AnalysisLogEntry = {
  id: string;
  user_id: string;
  image_url?: string; // Database column name
  disease: string;
  confidence: number;
  severity: string;
  treatment?: string;
  symptoms?: string[];
  prevention?: string;
  created_at?: string;
};

type EntryWithSignedUrl = AnalysisLogEntry & { signedUrl?: string };

export default function CropAnalysisHistoryModal({
  open,
  onClose,
  user,
  isAdmin = false,
  onSignIn,
}: {
  open: boolean;
  onClose: () => void;
  user: any;
  isAdmin?: boolean;
  onSignIn?: () => void;
}) {
  const [history, setHistory] = useState<EntryWithSignedUrl[]>([]);
  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editData, setEditData] = useState<Partial<AnalysisLogEntry>>({});
  const [error, setError] = useState<string | null>(null);

  // Fetch history and signed URLs
  useEffect(() => {
    if (!open) return;
    if (!isAdmin && (!user || !user.id)) {
      setLoading(false);
      setHistory([]);
      setError(null);
      return;
    }
    setLoading(true);

    const fetchHistory = async () => {
      setError(null);
      let query = supabase
        .from("crop_analyses")
        .select("*")
        .order("created_at", { ascending: false });

      if (!isAdmin) query = query.eq("user_id", user.id);

      const { data, error } = await query;
      if (error) {
        setHistory([]);
        setError("Failed to fetch analysis history.");
        setLoading(false);
        return;
      }

      // Fetch signed URLs for each entry
      if (data && data.length > 0) {
        const bucket = "analysis-images";
        const signedUrlResults = await Promise.all(
          data.map(async (entry: any) => {
            if (!entry.image_url) return { ...entry, signedUrl: undefined };
            const { data: signed } = await supabase.storage
              .from(bucket)
              .createSignedUrl(entry.image_url, 60 * 60); // 1 hour expiry
            return { ...entry, signedUrl: signed?.signedUrl };
          })
        );
        setHistory(signedUrlResults);
      } else {
        setHistory([]);
      }
      setLoading(false);
    };

    fetchHistory();
  }, [open, user, isAdmin]);

  // Delete functionality
  const handleDelete = async (id: string) => {
    if (!globalThis.confirm("Delete this analysis? This cannot be undone."))
      return;
    const { error } = await supabase
      .from("crop_analyses")
      .delete()
      .eq("id", id);
    if (!error) setHistory((h) => h.filter((item) => item.id !== id));
    else setError("Failed to delete entry.");
  };

  // Start editing
  const handleEdit = (entry: AnalysisLogEntry) => {
    setEditId(entry.id);
    setEditData({
      disease: entry.disease,
      confidence: entry.confidence,
      treatment: entry.treatment,
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditId(null);
    setEditData({});
  };

  // Save edit
  const handleSaveEdit = async (id: string) => {
    const { error } = await supabase
      .from("crop_analyses")
      .update(editData)
      .eq("id", id);
    if (!error) {
      setHistory((h) =>
        h.map((item) => (item.id === id ? { ...item, ...editData } : item))
      );
      setEditId(null);
      setEditData({});
      setError(null);
    } else {
      setError("Failed to update entry.");
    }
  };

  if (!open) return null;

  // If not logged in and not admin, show sign-in button
  if (!isAdmin && (!user || !user.id)) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
        <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden">
          <button
            type="button"
            className="absolute z-10 p-2 text-gray-500 rounded-full top-4 right-4 hover:bg-gray-100 hover:text-gray-700"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-7 h-7" />
          </button>
          <div className="flex items-center gap-3 px-8 pt-8 pb-4 mb-2 border-b border-gray-200">
            <History className="w-8 h-8 text-green-600" />
            <h2 className="text-2xl font-bold text-gray-800">
              Crop Analysis History
            </h2>
          </div>
          <div className="flex flex-col items-center justify-center flex-grow px-8 pb-8 overflow-y-auto">
            <button
              type="button"
              className="px-8 py-3 mt-16 text-base font-semibold text-white bg-green-600 rounded-md hover:bg-green-700"
              onClick={onSignIn}
            >
              Sign in to view your analysis history
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Normal: Show history for logged-in user, or all if admin
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-60 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden">
        <button
          type="button"
          className="absolute z-10 p-2 text-gray-500 rounded-full top-4 right-4 hover:bg-gray-100 hover:text-gray-700"
          onClick={onClose}
          aria-label="Close"
        >
          <X className="w-7 h-7" />
        </button>
        <div className="flex items-center gap-3 px-8 pt-8 pb-4 mb-2 border-b border-gray-200">
          <History className="w-8 h-8 text-green-600" />
          <h2 className="text-2xl font-bold text-gray-800">
            Crop Analysis History
          </h2>
        </div>
        <div className="flex-grow px-8 pb-8 overflow-y-auto">
          {error && (
            <div className="mb-4 text-center text-red-600">{error}</div>
          )}
          {loading ? (
            <div className="py-12 text-center text-gray-500">Loading...</div>
          ) : history.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              No analyses yet.
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry) => (
                <div
                  key={entry.id}
                  className="flex flex-col items-center gap-4 p-4 border border-gray-200 rounded-lg shadow-sm md:flex-row md:items-start bg-gray-50"
                >
                  <img
                    src={entry.signedUrl}
                    alt="Crop"
                    className="object-cover border border-gray-300 rounded-lg shadow w-28 h-28"
                  />
                  <div className="flex-1">
                    {editId === entry.id ? (
                      <>
                        <input
                          className="w-full px-2 py-1 mb-2 border rounded"
                          value={editData.disease || ""}
                          onChange={(e) =>
                            setEditData((d) => ({
                              ...d,
                              disease: e.target.value,
                            }))
                          }
                          placeholder="Disease"
                        />
                        <input
                          className="w-full px-2 py-1 mb-2 border rounded"
                          type="number"
                          min={0}
                          max={100}
                          step={1}
                          value={editData.confidence ?? ""}
                          onChange={(e) =>
                            setEditData((d) => ({
                              ...d,
                              confidence: parseFloat(e.target.value),
                            }))
                          }
                          placeholder="Confidence (0-100)"
                        />
                        <input
                          className="w-full px-2 py-1 mb-2 border rounded"
                          value={editData.treatment || ""}
                          onChange={(e) =>
                            setEditData((d) => ({
                              ...d,
                              treatment: e.target.value,
                            }))
                          }
                          placeholder="Treatment"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            className="flex items-center px-3 py-1 text-white bg-green-600 rounded hover:bg-green-700"
                            onClick={() => handleSaveEdit(entry.id)}
                          >
                            <Save className="w-4 h-4 mr-1" /> Save
                          </button>
                          <button
                            type="button"
                            className="flex items-center px-3 py-1 text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                            onClick={handleCancelEdit}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-lg font-bold text-green-800">
                            {entry.disease}
                          </span>
                          {typeof entry.confidence === "number" && (
                            <span
                              className={`inline-block px-3 py-1 text-xs rounded-full ${
                                entry.confidence >= 80
                                  ? "bg-green-100 text-green-700"
                                  : entry.confidence >= 50
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {entry.confidence}% confidence
                            </span>
                          )}
                        </div>
                        <div className="mb-1 text-sm text-gray-500">
                          {entry.created_at
                            ? new Date(entry.created_at).toLocaleString()
                            : ""}
                        </div>
                        <div className="text-sm text-gray-700">
                          <span className="font-semibold">Treatment: </span>
                          {entry.treatment}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex flex-row gap-2 mt-2 md:mt-0 md:flex-col">
                    {editId === entry.id ? null : (
                      <>
                        <button
                          type="button"
                          className="p-2 text-blue-700 bg-blue-100 rounded hover:bg-blue-200"
                          onClick={() => handleEdit(entry)}
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          className="p-2 text-red-700 bg-red-100 rounded hover:bg-red-200"
                          onClick={() => handleDelete(entry.id)}
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
